import { toast } from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getErrorMessage } from '@core/api/error-handler';
import { request } from '@core/api/request';

// AI agent personas — who the public assistant speaks as. CRUD lives at
// inbox/ai-agents/ (nested under inbox/, unlike channels/). `apiClient`'s
// baseURL already ends in `/api/v1/crm/` — do NOT prefix with `crm/`.
export type AgentTone = 'FRIENDLY' | 'PROFESSIONAL' | 'CONCISE';

export type AgentChannelKind = 'WIDGET' | 'INSTAGRAM' | 'FACEBOOK' | 'WHATSAPP';

export type AgentChannel = {
  id: number;
  channel: AgentChannelKind;
  external_id: string;
  display_name: string;
  ai_enabled: boolean;
  is_enabled: boolean;
};

export type AgentKbase = {
  id: number;
  name: string;
  is_active: boolean;
};

export interface AIAgent {
  id: number;
  name: string;
  /** Short job description, e.g. "Sales agent for exchange programs". */
  role: string;
  /** Org-authored prompt text; the backend injects it AFTER its safety
   * rules, which it can never override. */
  instructions: string;
  tone: AgentTone;
  /** The backend's own switch: an inactive agent never replies, even on a
   * channel or conversation it is assigned to. */
  is_active: boolean;
  knowledge_scoped?: boolean;
  knowledge_documents?: number[];
  kbases?: AgentKbase[];
  channels?: AgentChannel[];
  created_at: string;
  updated_at: string;
}

export type AgentInput = {
  name: string;
  /** No longer sent by the agent form: the backend stopped putting it in the
   * prompt and keeps the column only for existing rows. */
  role?: string;
  instructions: string;
  tone: AgentTone;
  is_active?: boolean;
  knowledge_scoped?: boolean;
  knowledge_documents?: number[];
  kbase_ids?: number[];
  channel_ids?: number[];
  // wait_time_before_responding?: number;
};

interface PaginatedEnvelope<T> {
  count: number;
  page_size: number;
  page_number: number;
  total_pages: number;
  results: T[];
}

export const AGENTS_QUERY_KEY = ['ai-agents'];

/** Same paginator-truncation defense channels-api.ts documents: the DRF
 * default page size would silently drop agents past the first page. */
const AGENTS_PAGE_SIZE = 100;

export const listAgents = async (): Promise<AIAgent[]> => {
  const response = await request<PaginatedEnvelope<AIAgent>>({
    url: 'inbox/ai-agents/',
    method: 'GET',
    params: { page_size: AGENTS_PAGE_SIZE },
  });
  return response.results;
};

export const useAgents = () => {
  return useQuery({
    queryKey: AGENTS_QUERY_KEY,
    queryFn: listAgents,
  });
};

export const getAgent = (id: number): Promise<AIAgent> => {
  return request<AIAgent>({
    url: `inbox/ai-agents/${id}/`,
    method: 'GET',
  });
};

export const useAgent = (id: number | null) => {
  return useQuery({
    queryKey: [...AGENTS_QUERY_KEY, 'one', id],
    queryFn: () => getAgent(id!),
    enabled: id != null,
  });
};

export const createAgent = async (data: AgentInput): Promise<AIAgent> => {
  return request<AIAgent>({
    url: 'inbox/ai-agents/',
    method: 'POST',
    body: data,
    // Structured, so a form can put each refused field under that field.
    preserveError: true,
  });
};

export const updateAgent = async (
  id: number,
  data: Partial<AgentInput>
): Promise<AIAgent> => {
  return request<AIAgent>({
    url: `inbox/ai-agents/${id}/`,
    method: 'PATCH',
    body: data,
    preserveError: true,
  });
};

/** DELETE with `preserveError`: a 409 means the agent is some channel's
 * default (the backend PROTECTs it) and callers show a different sentence
 * for that — the flattened string the interceptor makes by default would
 * erase the status they branch on. */
export const deleteAgent = async (id: number): Promise<void> => {
  await request<void>({
    url: `inbox/ai-agents/${id}/`,
    method: 'DELETE',
    preserveError: true,
  });
};

export const isChannelDefaultConflict = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 409;

/** A 400 that names the fields it refuses ({"name": ["An agent with this name
 * already exists."]}), as one message per field; null for anything else -- a
 * `detail`, a non-field error, another status, a network failure. */
export const agentFieldErrors = (
  error: unknown
): Record<string, string> | null => {
  if (!isAxiosError(error) || error.response?.status !== 400) return null;
  const data = error.response.data as unknown;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const entries = Object.entries(data as Record<string, unknown>);
  if (
    entries.length === 0 ||
    entries.some(([key]) => key === 'detail' || key === 'non_field_errors')
  ) {
    return null;
  }
  return Object.fromEntries(
    entries.map(([key, value]) => [
      key,
      Array.isArray(value) ? String(value[0]) : String(value),
    ])
  );
};

/** What a toast says for a failed save: the server's own words. */
const errorText = (error: unknown) =>
  typeof error === 'string' ? error : getErrorMessage(error as Error);

type SaveOptions = {
  /** The caller shows field errors under their fields (the agent form); only
   * an error that names no field becomes a toast. Without it every error
   * toasts -- the panels have no field to show one under. */
  inlineFieldErrors?: boolean;
};

const toastUnlessInline =
  ({ inlineFieldErrors = false }: SaveOptions) =>
  (error: unknown) => {
    if (inlineFieldErrors && agentFieldErrors(error)) return;
    toast.error(errorText(error));
  };

export const useCreateAgent = (options: SaveOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAgent,
    onSuccess: () => {
      toast.success('Agent created.');
    },
    onError: toastUnlessInline(options),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
    },
  });
};

export const useUpdateAgent = (options: SaveOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number } & Partial<AgentInput>) =>
      updateAgent(id, data),
    onSuccess: (saved) => {
      // The PATCH answers with the full row; put it in the cache now. Waiting for
      // the refetch left a window where the knowledge and channel panels built
      // their next toggle from the stale row, silently undoing the last one.
      queryClient.setQueryData<AIAgent[]>(AGENTS_QUERY_KEY, (rows) =>
        rows?.map((row) => (row.id === saved.id ? saved : row))
      );
      queryClient.setQueryData([...AGENTS_QUERY_KEY, 'one', saved.id], saved);
      toast.success('Agent updated.');
    },
    onError: toastUnlessInline(options),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
    },
  });
};

/** Turns one agent on or off through the backend's `is_active`. Optimistic:
 * the switch moves at once in the list and the open agent, and goes back
 * with a toast when the save fails, so the screen never claims a state the
 * backend does not hold. */
export const useSetAgentActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      updateAgent(id, { is_active }),
    onMutate: async ({ id, is_active }) => {
      const oneKey = [...AGENTS_QUERY_KEY, 'one', id];
      await queryClient.cancelQueries({ queryKey: AGENTS_QUERY_KEY });
      const list = queryClient.getQueryData<AIAgent[]>(AGENTS_QUERY_KEY);
      const one = queryClient.getQueryData<AIAgent>(oneKey);
      if (list) {
        queryClient.setQueryData<AIAgent[]>(
          AGENTS_QUERY_KEY,
          list.map((row) => (row.id === id ? { ...row, is_active } : row))
        );
      }
      if (one) queryClient.setQueryData<AIAgent>(oneKey, { ...one, is_active });
      return { list, one, oneKey };
    },
    onError: (_error, { is_active }, context) => {
      if (context?.list) {
        queryClient.setQueryData(AGENTS_QUERY_KEY, context.list);
      }
      if (context?.one) queryClient.setQueryData(context.oneKey, context.one);
      toast.error(
        is_active
          ? "Couldn't turn the agent on. It is still off."
          : "Couldn't turn the agent off. It is still on."
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
    },
  });
};

/** `useDeleteAgent` also invalidates `['conversations']`: the backend
 * SET_NULLs `ai_agent` on every conversation the deleted persona was
 * assigned to, so a thread already open in the inbox would otherwise keep
 * showing the "Assigned agent" fallback until its next unrelated refetch. */
export const useDeleteAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAgent,
    onSuccess: () => {
      toast.success('Agent deleted.');
    },
    onError: (error: unknown) => {
      toast.error(
        isChannelDefaultConflict(error)
          ? "This agent is a channel's default. Pick another default for that channel in Integrations first."
          : "Couldn't delete the agent. Please try again."
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};
